import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, Search, Heart, KeyRound, EyeOff, ArrowDown } from "lucide-react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { IMAGES } from "../lib/constants";
import { pick } from "../lib/format";
import { useSite } from "../context/SiteContext";
import { EnvCard } from "../components/EnvCard";

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.9, delay, ease: [0.2, 0.7, 0.2, 1] }} className={className}>
    {children}
  </motion.div>
);

const Hero = () => {
  const { t } = useTranslation();
  const { settings, lang } = useSite();
  return (
    <section className="relative min-h-[100svh] overflow-hidden bg-[var(--dusk)] grain" data-testid="hero-section">
      <img src={IMAGES.hero} alt="" className="absolute inset-0 h-full w-full object-cover kenburns" />
      <div className="absolute inset-0 bg-gradient-to-t from-[rgba(18,14,10,0.92)] via-[rgba(18,14,10,0.35)] to-[rgba(18,14,10,0.25)]" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 min-h-[100svh] flex flex-col justify-end pb-20 sm:pb-28 pt-32 text-[var(--cream)]">
        <p className="eyebrow !text-[var(--gold-soft)] anim-rise">{t("home.eyebrow")}</p>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.08] max-w-3xl mt-5 anim-rise" style={{ animationDelay: "0.15s" }} data-testid="hero-title">
          {pick(settings.hero_title, lang) || t("home.hero_title")}
        </h1>
        <p className="text-base md:text-lg max-w-xl mt-6 text-[var(--sand)]/90 leading-relaxed anim-rise" style={{ animationDelay: "0.3s" }} data-testid="hero-subtitle">
          {pick(settings.hero_subtitle, lang) || t("home.hero_subtitle")}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 mt-10 anim-rise" style={{ animationDelay: "0.45s" }}>
          <Link to="/create" className="btn-gold" data-testid="hero-create-btn">{t("home.cta_create")}</Link>
          <Link to="/explore" className="btn-glass" data-testid="hero-explore-btn">{t("home.cta_explore")}</Link>
        </div>
        <p className="mt-6 text-sm text-[var(--sand)]/75 flex items-center gap-2 anim-rise" style={{ animationDelay: "0.6s" }} data-testid="hero-free-note">
          <Check size={15} className="text-[var(--gold-soft)]" aria-hidden="true" /> {t("common.free_note")}
        </p>
        <a href="#places" className="hidden sm:flex absolute bottom-8 end-8 items-center gap-2 text-xs uppercase tracking-[0.25em] text-[var(--sand)]/60 hover:text-[var(--sand)]">
          {t("home.scroll")} <ArrowDown size={14} />
        </a>
      </div>
    </section>
  );
};

const Places = () => {
  const { t } = useTranslation();
  const [envs, setEnvs] = useState([]);
  useEffect(() => {
    api.get("/environments").then(({ data }) => setEnvs(data)).catch(() => {});
  }, []);
  const info = (k) => envs.find((e) => e.key === k);
  return (
    <section id="places" className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32" data-testid="environments-section">
      <Reveal className="max-w-2xl">
        <p className="eyebrow">{t("home.envs_eyebrow")}</p>
        <h2 className="font-heading text-3xl sm:text-4xl mt-4">{t("home.envs_title")}</h2>
        <p className="text-base md:text-lg text-[hsl(var(--muted-foreground))] mt-5 leading-relaxed">{t("home.envs_lead")}</p>
      </Reveal>
      <div className="grid lg:grid-cols-12 gap-5 mt-14">
        <Reveal className="lg:col-span-7"><EnvCard env="garden" info={info("garden")} size="lg" testId="home-env-garden" /></Reveal>
        <div className="lg:col-span-5 grid gap-5">
          <Reveal delay={0.1}><EnvCard env="forest" info={info("forest")} testId="home-env-forest" /></Reveal>
          <Reveal delay={0.2}><EnvCard env="shore" info={info("shore")} testId="home-env-shore" /></Reveal>
        </div>
      </div>
    </section>
  );
};

const How = () => {
  const { t } = useTranslation();
  const steps = [1, 2, 3];
  return (
    <section className="bg-[hsl(var(--secondary))]" data-testid="how-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32 grid lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-4">
          <p className="eyebrow">{t("home.how_eyebrow")}</p>
          <h2 className="font-heading text-3xl sm:text-4xl mt-4">{t("home.how_title")}</h2>
          <Link to="/create" className="btn-primary mt-8" data-testid="how-create-btn">{t("home.cta_create")}</Link>
        </Reveal>
        <ol className="lg:col-span-8 grid sm:grid-cols-3 gap-10">
          {steps.map((n, i) => (
            <Reveal key={n} delay={i * 0.12}>
              <li className="border-t border-[var(--gold)] pt-6">
                <span className="font-heading text-5xl text-[var(--gold)]">{n}</span>
                <h3 className="font-heading text-xl mt-4">{t(`home.step${n}_title`)}</h3>
                <p className="text-[hsl(var(--muted-foreground))] mt-3 leading-relaxed text-sm sm:text-base">{t(`home.step${n}_text`)}</p>
              </li>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  );
};

const PromiseBlock = () => {
  const { t } = useTranslation();
  const { settings, lang } = useSite();
  const items = [
    { icon: Heart, key: "promise_1" },
    { icon: KeyRound, key: "promise_2" },
    { icon: EyeOff, key: "promise_3" },
  ];
  const info = pick(settings.info_text, lang);
  return (
    <section className="relative bg-[var(--dusk)] text-[var(--cream)] grain overflow-hidden" data-testid="promise-section">
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32 grid lg:grid-cols-2 gap-16">
        <Reveal>
          <blockquote className="font-heading italic text-3xl sm:text-4xl leading-snug text-[var(--sand)]">“{t("home.quote")}”</blockquote>
          <p className="mt-6 eyebrow !text-[var(--gold-soft)]">— {t("home.quote_author")}</p>
          {info && <p className="mt-10 text-[var(--sand)]/80 leading-relaxed max-w-lg" data-testid="home-info-text">{info}</p>}
        </Reveal>
        <div>
          <Reveal><h2 className="font-heading text-2xl sm:text-3xl">{t("home.promise_title")}</h2></Reveal>
          <ul className="mt-8 space-y-6">
            {items.map(({ icon: Icon, key }, i) => (
              <Reveal key={key} delay={i * 0.1}>
                <li className="flex gap-4">
                  <span className="h-11 w-11 shrink-0 rounded-full border border-white/20 grid place-items-center text-[var(--gold-soft)]"><Icon size={18} /></span>
                  <p className="text-[var(--sand)]/90 leading-relaxed pt-2">{t(`home.${key}`)}</p>
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

const SearchBlock = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  return (
    <section className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32" data-testid="search-section">
      <Reveal className="max-w-3xl">
        <p className="eyebrow">{t("home.search_eyebrow")}</p>
        <h2 className="font-heading text-3xl sm:text-4xl mt-4">{t("home.search_title")}</h2>
        <p className="text-base md:text-lg text-[hsl(var(--muted-foreground))] mt-4">{t("home.search_lead")}</p>
        <form className="mt-10 flex flex-col sm:flex-row gap-3" role="search" onSubmit={(e) => { e.preventDefault(); navigate(`/memorials?q=${encodeURIComponent(q)}`); }}>
          <label className="relative flex-1">
            <span className="sr-only">{t("home.search_placeholder")}</span>
            <Search size={18} className="absolute start-5 top-1/2 -translate-y-1/2 opacity-50" aria-hidden="true" />
            <input className="field-input !rounded-full ps-12 py-4" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("home.search_placeholder")} data-testid="home-search-input" />
          </label>
          <button className="btn-primary" type="submit" data-testid="home-search-btn">{t("common.search")}</button>
        </form>
      </Reveal>
    </section>
  );
};

export default function Home() {
  return (
    <>
      <Hero />
      <Places />
      <How />
      <SearchBlock />
      <PromiseBlock />
    </>
  );
}
